import { intl } from '@fullstory/intl';
// TODO(jaime): Feels like an opportunity to share enumerated types with the server definitions.
// Also the recording definitions.

import { CustomField, FieldScope, VarType } from './types';

const FieldScope_vals = initValLookup(FieldScope);
const VarType_vals = initValLookup(VarType);

export const customFieldCompareFn = (a: CustomField, b: CustomField) => {
  const c = a.ident.localeCompare(b.ident);
  if (c != 0) {
    return c;
  }
  return a.varType.localeCompare(b.varType);
};

// Format the custom field for printing. Optionally display the field's scope in
// the display text. Optionally shortens the ident to a maximum length (must be greater than 15)
export function prettifyCustomField(
  field: CustomField,
  includeScope?: boolean,
  maxIdentLength?: number,
  stripParents?: boolean,
): string {
  const fieldType = prettifyVarType(field.varType);
  const strippedParent = field.ident.split('.').slice(-1)[0];

  let ident: string = stripParents && strippedParent !== undefined ? strippedParent : field.ident;

  if (maxIdentLength) {
    ident = shortenDisplayIdent(ident, maxIdentLength);
  }

  if (includeScope) {
    return `${ident} ${intl.formatMessage(
      {
        defaultMessage: '({fieldType}, {fieldScope} field)',
        id: 'XQNsIh',
        description: 'Describes a data type field (e.g boolean, page field)',
      },
      { fieldType, fieldScope: field.scope },
    )}`;
  }

  return `${ident} (${fieldType})`;
}

export function shortenDisplayIdent(ident: string, maxIdentLength: number): string {
  // Ensure maxIdentLength is at least 15; otherwise formatter would break
  if (maxIdentLength > 15 && ident.length > maxIdentLength) {
    return `${ident.substring(0, 10)}...${ident.substring(ident.length - maxIdentLength + 13)}`;
  }

  return ident;
}

export function prettifyVarType(type: VarType, verbose?: boolean): string {
  switch (type) {
    case VarType.Str:
      return intl.formatMessage({
        defaultMessage: 'string',
        id: 'JC6N6C',
        description: 'Data type',
      });
    case VarType.Bool:
      return verbose
        ? intl.formatMessage({ defaultMessage: 'boolean', id: 'yKY/X0', description: 'Data type' })
        : intl.formatMessage({ defaultMessage: 'bool', id: 'u7yinD', description: 'Data type' });
    case VarType.Int:
      return verbose
        ? intl.formatMessage({ defaultMessage: 'integer', id: 'vp90dI', description: 'Data type' })
        : intl.formatMessage({ defaultMessage: 'int', id: 'jkMNeu', description: 'Data type' });
    case VarType.Real:
      return intl.formatMessage({
        defaultMessage: 'real',
        id: 'f1YVtd',
        description: 'Data type',
      });
    case VarType.Date:
      return intl.formatMessage({ defaultMessage: 'date', id: 'wkOY+t', description: 'Data type' });
    case VarType.Strs:
      return intl.formatMessage({
        defaultMessage: 'strings',
        id: 'Sdld/o',
        description: 'Data type',
      });
    case VarType.Bools:
      return verbose
        ? intl.formatMessage({ defaultMessage: 'booleans', id: 'shNsRe', description: 'Data type' })
        : intl.formatMessage({ defaultMessage: 'bools', id: 'N2P1jW', description: 'Data type' });
    case VarType.Ints:
      return verbose
        ? intl.formatMessage({ defaultMessage: 'integers', id: 'sDQmUR', description: 'Data type' })
        : intl.formatMessage({ defaultMessage: 'ints', id: '0u26S0', description: 'Data type' });
    case VarType.Reals:
      return intl.formatMessage({
        defaultMessage: 'reals',
        id: 'qX/02f',
        description: 'Data type',
      });
    case VarType.Dates:
      return intl.formatMessage({
        defaultMessage: 'dates',
        id: 'nI2QL7',
        description: 'Data type',
      });
  }
}

// Will optionally de-scope and de-suffix a custom fieldName and return the scope, field ident, and type suffix.
//
// If the fieldName does not contain a valid scope or type suffix, it will return "" for the scope/type suffix
// and either the original fieldName as the fieldIdent if neither are valid (or the parts left over if one is valid).
//
// NOTE: That underscores are allowed characters. So there is the possibility that there can be collisions in scope and
// var type suffix with custom field names. This is a big smell, but one we live with. If the prefix or suffix lines up
// with an expected scope of vartype, we treat it as such.
export function parseFieldName(fieldName: string): CustomField {
  const parsed: CustomField = {
    scope: FieldScope.NoScope,
    ident: fieldName,
    varType: VarType.Str,
  };

  // See if field belongs to a parent. Those have a `:parent` suffix.
  // Chomp the parent off, it should be handled separately.
  const colonIdx = fieldName.indexOf(':');
  if (colonIdx > 0) {
    fieldName = fieldName.substring(0, colonIdx);
  }

  // Chomp off the scope. And use it if it looks like a valid scope.
  const firstUnderscore = fieldName.indexOf('_');
  if (firstUnderscore > 0) {
    const scope = fieldName.substring(0, firstUnderscore);
    if (FieldScope_vals[scope]) {
      parsed.scope = <FieldScope>scope; // The value is the enum.
      parsed.ident = fieldName.substring(firstUnderscore + 1);
    }
  }

  // Chomp off the varType. And use it if it looks like a valid varType.
  const lastUnderscore = parsed.ident.lastIndexOf('_');
  if (lastUnderscore > 0) {
    const varType = parsed.ident.substring(lastUnderscore + 1);
    if (VarType_vals[varType]) {
      parsed.varType = <VarType>varType; // The value is the enum.
      parsed.ident = parsed.ident.substring(0, lastUnderscore);
    }
  }

  return parsed;
}

export function parseUnscopedFieldName(fieldName: string, scope: FieldScope): CustomField {
  const parsed: CustomField = {
    scope,
    ident: fieldName,
    varType: VarType.Str,
  };

  // Chomp off the varType. And use it if it looks like a valid varType.
  const lastUnderscore = parsed.ident.lastIndexOf('_');
  if (lastUnderscore > 0) {
    const varType = parsed.ident.substring(lastUnderscore + 1);
    if (VarType_vals[varType]) {
      parsed.varType = <VarType>varType; // The value is the enum.
      parsed.ident = parsed.ident.substring(0, lastUnderscore);
    }
  }

  return parsed;
}

// Returns a value -> enum code name key lookup table for a given String enum.
function initValLookup(stringEnumMap: typeof VarType | typeof FieldScope): {
  [key: string]: string;
} {
  const lookup: { [key: string]: string } = {};
  for (const [key, value] of Object.entries(stringEnumMap)) {
    lookup[value] = key;
  }
  return lookup;
}

const escapeQuotes = (str: string) => `"${str.replace(/"/g, '""')}"`;

export function fmtCustomValueForExport(parsedField: CustomField, value: any): any {
  switch (parsedField.varType) {
    case VarType.Str:
      // Strings need to be quoted and escaped for csv.
      return escapeQuotes(value);
    case VarType.Bool:
    case VarType.Int:
    case VarType.Real:
    case VarType.Date:
      // The rest of the atomic values are fine as is.
      return value;
    case VarType.Strs:
    case VarType.Bools:
    case VarType.Ints:
    case VarType.Reals:
    case VarType.Dates:
      // Repeated vars just need to be json stringified, quoted, and escaped to work for csv.
      return escapeQuotes(JSON.stringify(value));
  }
}

export function scopedTypedCustomFieldIdent(customField: CustomField): string {
  return `${customField.scope ? `${customField.scope}_` : ''}${customField.ident}_${
    customField.varType
  }`;
}

export function unscopedCustomFieldIdent(customField: CustomField): string {
  return `${customField.ident}_${customField.varType}`;
}

export * from './hidden';
export * from './types';
